import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { Maybe, isArdoqError, pluralize } from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { workspaceUtils } from '@ardoq/scope-data';
import { type I18nLocale, t } from '@ardoq/i18n';
import {
  ContextMenuInventoryCommands,
  InventoryAccessControl,
  InventoryDatasourceSelectionType,
} from './types';

export type ComponentButtonProps = {
  name: string;
  iconName: IconName;
  isDisabled: boolean;
  tooltip?: string;
  onClick: () => void | Promise<void>;
};

type GetComponentButtonProps = {
  components: APIComponentAttributes[];
  accessControl: InventoryAccessControl;
  scopeData: Maybe<EnhancedScopeData>;
  commands: ContextMenuInventoryCommands;
  i18nLocale: I18nLocale;
  isLockingEnabled: boolean;
};

const getLockedTooltip = (i18nLocale: I18nLocale): string =>
  t(
    i18nLocale,
    'inventoryApp:ag.contextMenu.tooltip.locked',
    'One or more selected components are locked'
  );

const getLacksEditPermissionTooltip = (i18nLocale: I18nLocale): string =>
  t(
    i18nLocale,
    'inventoryApp:ag.contextMenu.tooltip.lacksEditPermission',
    'You do not have edit permission for one or more selected components'
  );

const getChangeParentTooltip = (
  lacksEditPermission: boolean,
  hasMultipleWorkspaces: boolean,
  isLocked: boolean,
  isRigid: boolean,
  i18nLocale: I18nLocale
): string | undefined => {
  if (isLocked) {
    return getLockedTooltip(i18nLocale);
  }
  if (lacksEditPermission) {
    return getLacksEditPermissionTooltip(i18nLocale);
  }
  if (hasMultipleWorkspaces) {
    return t(
      i18nLocale,
      'inventoryApp:ag.contextMenu.tooltip.changeParentMultipleWorkspaces',
      'Cannot change parent for components from different workspaces'
    );
  }
  if (isRigid) {
    return t(
      i18nLocale,
      'inventoryApp:ag.contextMenu.tooltip.changeParentRigidWorkspace',
      'Cannot change parent for components in a rigid workspace'
    );
  }
  return undefined;
};

const getDeleteTooltip = (
  isLocked: boolean,
  lacksEditPermission: boolean,
  i18nLocale: I18nLocale
): string | undefined => {
  if (isLocked) {
    return getLockedTooltip(i18nLocale);
  }
  if (lacksEditPermission) {
    return getLacksEditPermissionTooltip(i18nLocale);
  }
  return undefined;
};

const getMergeTooltip = (
  hasTooFewComponents: boolean,
  isLocked: boolean,
  lacksEditPermission: boolean,
  i18nLocale: I18nLocale
): string | undefined => {
  if (hasTooFewComponents) {
    return t(
      i18nLocale,
      'inventoryApp:ag.contextMenu.tooltip.mergeTooFew',
      'Select at least 2 components to merge'
    );
  }
  if (isLocked) {
    return getLockedTooltip(i18nLocale);
  }
  if (lacksEditPermission) {
    return getLacksEditPermissionTooltip(i18nLocale);
  }
  return undefined;
};

const getDuplicateTooltip = (
  lacksEditPermission: boolean,
  i18nLocale: I18nLocale
): string | undefined => {
  if (lacksEditPermission) {
    return getLacksEditPermissionTooltip(i18nLocale);
  }
  return undefined;
};

const getAddChildTooltip = (
  lacksEditPermission: boolean,
  hasMultipleComponentsSelected: boolean,
  i18nLocale: I18nLocale
): string | undefined => {
  if (hasMultipleComponentsSelected) {
    return t(
      i18nLocale,
      'inventoryApp:ag.contextMenu.tooltip.addChildSingleOnly',
      'You can only add a child to a single component'
    );
  }
  if (lacksEditPermission) {
    return t(
      i18nLocale,
      'inventoryApp:ag.contextMenu.tooltip.addChildLacksEditPermission',
      'You do not have edit permission for the selected component'
    );
  }
  return undefined;
};

export const componentButtonProps = {
  getDeleteButtonProps: ({
    components,
    accessControl,
    commands,
    agGridDeselectAll,
    agGridServerSideRefresh,
    agGridRemovePinnedRows,
    i18nLocale,
    isLockingEnabled,
  }: GetComponentButtonProps & {
    agGridDeselectAll: VoidFunction;
    agGridServerSideRefresh: VoidFunction;
    agGridRemovePinnedRows: (componentIds: ArdoqId[]) => void;
  }): ComponentButtonProps => {
    const isLocked =
      isLockingEnabled && components.some(component => component.lock);
    const lacksEditPermission = components.some(
      component => !accessControl.hasEditPermissionForComponent(component)
    );

    return {
      name: t(
        i18nLocale,
        'inventoryApp:ag.contextMenu.action.delete',
        { count: components.length },
        `Delete ${pluralize('component', components.length)}`
      ),
      iconName: IconName.DELETE,
      isDisabled: isLocked || lacksEditPermission,
      tooltip: getDeleteTooltip(isLocked, lacksEditPermission, i18nLocale),
      onClick: async () => {
        const result =
          await commands.launchDeleteComponentsAndReferencesConfirmation(
            components
          );
        if (result) {
          agGridDeselectAll();
          agGridServerSideRefresh();
          if (typeof result === 'object' && 'componentIds' in result) {
            agGridRemovePinnedRows(result.componentIds);
          }
        }
      },
    };
  },

  getCreateScenarioButtonProps: ({
    components,
    accessControl,
    commands,
    i18nLocale,
  }: GetComponentButtonProps): ComponentButtonProps | null =>
    accessControl.canCreateScenario
      ? {
          name: t(
            i18nLocale,
            'inventoryApp:ag.contextMenu.action.createScenario',
            'Create scenario from selection'
          ),
          iconName: IconName.SCENARIO,
          isDisabled: components.length === 0,
          onClick: () => {
            commands.createScenarioFromComponents(components);
          },
        }
      : null,

  getAddToScenarioButtonProps: ({
    components,
    accessControl,
    commands,
    i18nLocale,
  }: GetComponentButtonProps): ComponentButtonProps | null =>
    accessControl.canAddToExistingScenario
      ? {
          name: t(
            i18nLocale,
            'inventoryApp:ag.contextMenu.action.addToScenario',
            'Add selection to scenario'
          ),
          iconName: IconName.SCENARIO,
          isDisabled: components.length === 0,
          onClick: () => {
            commands.addComponentsToExistingScenario(components);
          },
        }
      : null,

  getViewHistoryButtonProps: ({
    components,
    commands,
    i18nLocale,
  }: GetComponentButtonProps): ComponentButtonProps => ({
    name: t(
      i18nLocale,
      'inventoryApp:ag.contextMenu.action.viewHistory',
      'View history'
    ),
    iconName: IconName.HISTORY,
    isDisabled: false,
    onClick: () => {
      commands.viewComponentsInAuditLog(components);
    },
  }),

  getMergeButtonProps: ({
    components,
    accessControl,
    scopeData,
    commands,
    i18nLocale,
    isLockingEnabled,
  }: GetComponentButtonProps): ComponentButtonProps => {
    const hasTooFewComponents = components.length < 2;
    const isLocked =
      isLockingEnabled && components.some(component => component.lock);
    const lacksEditPermission = components.some(
      component => !accessControl.hasEditPermissionForComponent(component)
    );

    return {
      name: t(
        i18nLocale,
        'inventoryApp:ag.contextMenu.action.merge',
        'Merge components'
      ),
      iconName: IconName.SCENARIO_MERGE,
      isDisabled:
        hasTooFewComponents || isLocked || lacksEditPermission || !scopeData,
      tooltip: getMergeTooltip(
        hasTooFewComponents,
        isLocked,
        lacksEditPermission,
        i18nLocale
      ),
      onClick: () => {
        commands.openEntityMergeModal(components, scopeData!);
      },
    };
  },

  getDuplicateButtonProps: ({
    components,
    accessControl,
    scopeData,
    commands,
    agGridDeselectAll,
    agGridServerSideRefresh,
    dataSourceType,
    i18nLocale,
  }: GetComponentButtonProps & {
    agGridDeselectAll: VoidFunction;
    agGridServerSideRefresh: VoidFunction;
    dataSourceType: InventoryDatasourceSelectionType;
  }): ComponentButtonProps => {
    const lacksEditPermission = components.some(
      component => !accessControl.canEditWorkspace(component.rootWorkspace)
    );

    return {
      name: t(
        i18nLocale,
        'inventoryApp:ag.contextMenu.action.duplicate',
        { count: components.length },
        `Duplicate ${pluralize('component', components.length)}`
      ),
      iconName: IconName.COPY,
      isDisabled: components.length < 1 || lacksEditPermission || !scopeData,
      tooltip: getDuplicateTooltip(lacksEditPermission, i18nLocale),
      onClick: async () => {
        const result = await commands.duplicateComponents(
          components,
          scopeData!,
          dataSourceType
        );

        if (isArdoqError(result)) {
          return;
        }

        agGridDeselectAll();
        agGridServerSideRefresh();
      },
    };
  },

  getAddChildButtonProps: ({
    components,
    accessControl,
    scopeData,
    commands,
    i18nLocale,
  }: GetComponentButtonProps): ComponentButtonProps => {
    const lacksEditPermission = components.some(
      component => !accessControl.canEditWorkspace(component.rootWorkspace)
    );
    const hasMultipleComponentsSelected = components.length !== 1;

    return {
      name: t(
        i18nLocale,
        'inventoryApp:ag.contextMenu.action.addChild',
        'Add child'
      ),
      iconName: IconName.ADD,
      isDisabled:
        hasMultipleComponentsSelected || lacksEditPermission || !scopeData,
      tooltip: getAddChildTooltip(
        lacksEditPermission,
        hasMultipleComponentsSelected,
        i18nLocale
      ),
      onClick: () => {
        commands.openAddChildDialog(components[0], scopeData!);
      },
    };
  },

  getChangeParentButtonProps: ({
    components,
    accessControl,
    scopeData,
    commands,
    i18nLocale,
    isLockingEnabled,
  }: GetComponentButtonProps): ComponentButtonProps => {
    const lacksEditPermission = components.some(
      component => !accessControl.hasEditPermissionForComponent(component)
    );
    const isLocked =
      isLockingEnabled && components.some(component => component.lock);
    const workspaceIds = [
      ...new Set(components.map(component => component.rootWorkspace)),
    ];
    const hasMultipleWorkspaces = workspaceIds.length > 1;
    const isRigid =
      Boolean(scopeData) &&
      workspaceIds.some(
        workspaceId =>
          !workspaceUtils.isFlexibleWorkspace(scopeData!, workspaceId)
      );

    return {
      name: t(
        i18nLocale,
        'inventoryApp:ag.contextMenu.action.changeParent',
        'Change parent'
      ),
      iconName: IconName.PARENT_CHILD,
      isDisabled:
        components.length < 1 ||
        lacksEditPermission ||
        hasMultipleWorkspaces ||
        isRigid ||
        !scopeData,
      tooltip: getChangeParentTooltip(
        lacksEditPermission,
        hasMultipleWorkspaces,
        isLocked,
        isRigid,
        i18nLocale
      ),
      onClick: () => {
        commands.openSelectParentDialog(components, scopeData!);
      },
    };
  },
};
